import { Box, Chip, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  TRIAGE_AMBER_HEX,
  TRIAGE_RED_HEX,
  hasTriageMarks,
  triageChipLabel,
  triageTooltipText
} from './triageMarkText';

/**
 * The shared mark for a row that carries unread triage cards (spec §6.1).
 * One component, two surfaces: the Descalces table and the opener group table.
 *
 * 🚨 It renders NOTHING at zero and NOTHING when `counts` is `undefined`. The
 * two cases look identical here on purpose — the difference is stated once, in
 * the surface's header, by `TriageIndexNotice`. A chip that guessed at an
 * unreadable index would be the one lie this feature exists to prevent.
 */
export interface TriageMarksProps {
  /** `undefined` ⇒ the index could not be read ⇒ NOTHING renders (the header says so). */
  counts: { counter: number; orphan: number; latestAt: string | null } | undefined;
  /**
   * Who owns the `<Tooltip>`. Only the opener TABLE bans passive popovers and
   * wraps this in its own gated one; the Descalces page and the group pickers
   * have no such ban, and without a tooltip of their own their badges are mute
   * — the `unattributedNote` line included.
   */
  variant: 'descalce' | 'opener' | 'picker';
  /** Show the "could not be attributed to a rule" line. */
  unattributedNote?: boolean;
  /** Red when the row is unhedged right now, amber otherwise. */
  colorHint?: 'red' | 'amber';
  /** Deep-link into the triage tray. */
  deepLinkTo?: string;
}

export function TriageMarks({
  counts,
  variant,
  unattributedNote,
  colorHint,
  deepLinkTo
}: TriageMarksProps): JSX.Element | null {
  if (!hasTriageMarks(counts)) return null;

  const tone = colorHint === 'red' ? TRIAGE_RED_HEX : TRIAGE_AMBER_HEX;
  const tooltip = triageTooltipText(counts, {
    unattributedNote,
    subject: variant === 'picker' ? 'group' : 'row'
  });

  const chip = (
    <Chip
      size="small"
      label={triageChipLabel(counts)}
      variant="outlined"
      data-triage-marks="true"
      data-triage-counter={counts.counter}
      data-triage-orphan={counts.orphan}
      data-triage-color={colorHint ?? 'amber'}
      data-triage-tooltip={tooltip}
      sx={{
        height: 18,
        fontSize: '0.62rem',
        fontWeight: 700,
        color: tone,
        borderColor: tone,
        cursor: deepLinkTo ? 'pointer' : 'default',
        '& .MuiChip-label': { px: 0.6 }
      }}
    />
  );

  // 🚨 A router `Link`, never a bare `<a href>`: React Router does not
  // intercept a native anchor, so every mark — opener row, group landing, the
  // `GroupSelect` menu, Descalces — was a full page load. It renders the same
  // `<a href>`, and inside the picker's `<Menu>` it is the difference between
  // opening the tray and re-booting the SPA.
  const marks = deepLinkTo ? (
    <Box
      component={RouterLink}
      to={deepLinkTo}
      data-triage-link={deepLinkTo}
      sx={{ textDecoration: 'none', display: 'inline-flex' }}
    >
      {chip}
    </Box>
  ) : (
    chip
  );

  // The opener table bans passive popovers: its call site wraps this in the
  // whitelisted `<Tooltip … disableHoverListener={!hasTriageMarks} />`, so a
  // second tooltip here would both double the popover and break that audit.
  // That ban is the TABLE's — `openerHoversSimOnly.test.ts` reads
  // `EntryTabByPath.tsx` and nothing else — so the pickers tooltip normally.
  if (variant === 'opener') return marks;

  return (
    <Tooltip title={tooltip} placement="top">
      {marks}
    </Tooltip>
  );
}
